import React, { useMemo, useState } from 'react';
import { supabase } from '../services/supabaseClient';
import { useAuth } from '../context/AuthContext';

const AdminProfilePage = () => {
  const { user, role } = useAuth();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState({ type: null, message: '' });
  const [updating, setUpdating] = useState(false);

  const loginEmail = user?.email || '';
  const personalEmail = user?.user_metadata?.personal_email || '';

  const roleLabel = useMemo(() => {
    if (!role) return 'guest';
    return role;
  }, [role]);

  const clearStatus = () => setStatus({ type: null, message: '' });

  const handlePasswordUpdate = async (event) => {
    event.preventDefault();
    clearStatus();

    if (!newPassword || newPassword.length < 8) {
      setStatus({ type: 'error', message: 'Password must be at least 8 characters long.' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setStatus({ type: 'error', message: 'Passwords do not match.' });
      return;
    }

    setUpdating(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setStatus({ type: 'success', message: 'Password updated successfully.' });
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Failed to update password. Please try again.'
      });
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900">Profile &amp; Security</h1>
          <p className="text-slate-600 mt-2">
            Manage your administrator account settings and security preferences.
          </p>
        </div>

        <div className="grid gap-6">
          <section className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-6 py-5 border-b border-slate-100">
              <h2 className="text-lg font-semibold text-slate-900">Account Overview</h2>
              <p className="text-sm text-slate-500 mt-1">
                Review your login details and associated information.
              </p>
            </div>
            <dl className="divide-y divide-slate-100">
              <div className="px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <dt className="text-sm font-medium text-slate-600">
                  Login Email
                </dt>
                <dd className="text-sm text-slate-900 font-semibold break-all">
                  {loginEmail || 'Not available'}
                </dd>
              </div>
              <div className="px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <dt className="text-sm font-medium text-slate-600">
                  Personal Email
                </dt>
                <dd className="text-sm text-slate-900 break-all">
                  {personalEmail || '—'}
                </dd>
              </div>
              <div className="px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <dt className="text-sm font-medium text-slate-600">
                  Role
                </dt>
                <dd className="inline-flex items-center gap-2 text-sm font-semibold text-green-600 bg-green-50 border border-green-100 rounded-full px-3 py-1">
                  <span className="text-base" aria-hidden="true">
                    🛡️
                  </span>
                  {roleLabel}
                </dd>
              </div>
              <div className="px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <dt className="text-sm font-medium text-slate-600">
                  Last Sign-in
                </dt>
                <dd className="text-sm text-slate-900">
                  {user?.last_sign_in_at
                    ? new Date(user.last_sign_in_at).toLocaleString()
                    : '—'}
                </dd>
              </div>
            </dl>
          </section>

          <section className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-6 py-5 border-b border-slate-100">
              <h2 className="text-lg font-semibold text-slate-900">Security</h2>
              <p className="text-sm text-slate-500 mt-1">
                Update your password regularly to keep your admin access secure.
              </p>
            </div>
            <form className="px-6 py-6 space-y-6" onSubmit={handlePasswordUpdate}>
              {status.type && (
                <div
                  className={`rounded-lg px-4 py-3 text-sm ${
                    status.type === 'success'
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                      : 'bg-red-50 text-red-700 border border-red-100'
                  }`}
                >
                  {status.message}
                </div>
              )}
              <div className="grid gap-4 md:grid-cols-2">
                <label className="flex flex-col gap-2">
                  <span className="text-sm font-medium text-slate-700">
                    New Password
                  </span>
                  <input
                    type="password"
                    className="w-full rounded-lg border border-slate-200 px-4 py-2.5 text-slate-900 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-60"
                    value={newPassword}
                    onChange={(event) => setNewPassword(event.target.value)}
                    placeholder="At least 8 characters"
                    autoComplete="new-password"
                    disabled={updating}
                  />
                </label>
                <label className="flex flex-col gap-2">
                  <span className="text-sm font-medium text-slate-700">
                    Confirm Password
                  </span>
                  <input
                    type="password"
                    className="w-full rounded-lg border border-slate-200 px-4 py-2.5 text-slate-900 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-60"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    autoComplete="new-password"
                    disabled={updating}
                  />
                </label>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <p className="text-xs text-slate-500">
                  Tip: Use a strong, unique password that you haven’t used elsewhere. Consider a password manager for secure storage.
                </p>
                <button
                  type="submit"
                  className="btn btn-accent text-sm px-5 py-2"
                  disabled={updating}
                >
                  {updating ? 'Updating…' : 'Update Password'}
                </button>
              </div>
            </form>
          </section>
        </div>
      </div>
    </div>
  );
};

export default AdminProfilePage;
